import {Observable, Subscription, filter} from 'rxjs';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {NavigationEnd, Router} from '@angular/router';
import {AnalyseStarterGuard} from './core/guard/analyse-starter.guard';
import {ResultGuard} from './core/guard/result.guard';
import {AnalyseStarterService} from 'shared/services/analyse-starter.service';
import {DataStorageService} from 'shared/services/data-storage.service';
import {WizardService} from 'shared/services/wizard.service';
import {MessageService} from 'shared/services/message.service';
import {DirectModuleSelectionService} from 'shared/services/direct-module-selection/direct-module-selection.service';
import {HttpClient} from '@angular/common/http';
import {ReleaseInfo} from 'shared/dtos/release-info';
import {MatDialog} from '@angular/material/dialog';

import {
  ObINavigationLink,
  ObMasterLayoutConfig,
  ObNavTreeItemModel
} from '@oblique/oblique';
import {map} from 'rxjs/operators';
import {MarkdownDialogComponent} from './changelog/markdown-dialog.component';
import {NavigationService} from 'shared/services/navigation.service';

interface ObILanguage {
  code: string;
  label: string;
  id?: string;
}

interface ObILocaleObject {
  locale: string;
  id?: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  navigation: ObINavigationLink[] = [];

  public release: string;
  languages: ObILanguage[];
  fileFolder = 'assets/Data/';

  displayTitle: string | undefined = '';

  public items: ObNavTreeItemModel[];
  public today = Date.now();
  public kontrollmodus: boolean;
  public homeLinkHidden = false;

  private readonly tree = {
    label: 'Navigation Tree',
    items: []
  };
  private wizardSubscription: Subscription;
  private analysisSubscription: Subscription;
  private directModuleSelectionSubscription: Subscription;
  private routerSub: Subscription;
  private _fileLangKey = 'd';
  private _analyseEnabled = false;
  private _resultEnabled = false;

  constructor(
    private readonly wizard: WizardService,
    private readonly resultGuard: ResultGuard,
    private readonly analysisGuard: AnalyseStarterGuard,
    private readonly analyseStartedService: AnalyseStarterService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly renderer: Renderer2,
    private readonly storage: DataStorageService,
    private readonly messageService: MessageService,
    private readonly directModuleSelection: DirectModuleSelectionService,
    private readonly httpClient: HttpClient,
    private readonly matDialog: MatDialog,
    private readonly config: ObMasterLayoutConfig,
    private readonly router: Router,
    private readonly navigationService: NavigationService
  ) {
    this.languages = this.formatLanguages(this.config.locale.languages);
  }

  public get fileLangKey(): string {
    return this._fileLangKey;
  }

  public get analyseEnabled(): boolean {
    return this._analyseEnabled;
  }

  public get resultEnabled(): boolean {
    return this._resultEnabled;
  }

  public get isLandingPage(): boolean {
    return this.router.routerState.snapshot.url === '/';
  }

  public get isForModule2(): 'm1' | 'm2' | 'home' | 'landing-page' {
    if (this.router.routerState.snapshot.url === '/') {
      return 'landing-page';
    }
    if (
      !this.router.routerState.snapshot.url.includes('/m1') &&
      !this.router.routerState.snapshot.url.includes('/m2')
    ) {
      return 'home';
    }
    switch (this.analyseStartedService.isForModule2) {
      case true:
        return 'm2';
      case false:
        return 'm1';
      case null:
      default:
        return 'home';
    }
  }

  ngOnInit(): void {
    void this.initializeAsync();
  }

  ngAfterViewInit() {
    const iconHelpBtn = document.querySelector('#iconHelp');
    if (iconHelpBtn) {
      const helpIconTitle = iconHelpBtn.childNodes[0].childNodes[0];
      helpIconTitle.parentNode.removeChild(helpIconTitle);
    }

    this.disableNavigationLinks();
  }

  ngOnDestroy(): void {
    this.wizardSubscription?.unsubscribe();
    this.analysisSubscription?.unsubscribe();
    this.directModuleSelectionSubscription?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  isLangActive(lang: string): boolean {
    return this.translate.currentLang === lang;
  }

  changeLang(lang: string): void {
    this.translate.use(lang);
  }

  setResultDisplay(visible: boolean) {
    this._resultEnabled = visible;
  }

  setAnalyseEnabled(enabled: boolean) {
    this._analyseEnabled = enabled;
  }

  showChangelogDialog() {
    this.httpClient
      .get('assets/changelog.md', {responseType: 'text'})
      .subscribe(markdownContent => {
        this.matDialog.open(MarkdownDialogComponent, {
          width: '70vw',
          maxWidth: '70vw',
          data: {markdown: markdownContent}
        });
      });
  }

  getTranslatedHref$(key: string): Observable<string> {
    return this.translate
      .stream(key)
      .pipe(map(res => `${this.fileFolder}${res}`));
  }

  async setExpertCockpit() {
    this.kontrollmodus = !this.kontrollmodus;
    this.storage.storeKontrollmodus(this.kontrollmodus);
    this.messageService.getSendCockpitTabExperten(this.kontrollmodus);
  }

  async setReleasenumber() {
    const url = 'api/releaseInfo/releaseinfo';
    this.httpClient.get(url).subscribe({
      next: (data: ReleaseInfo) => {
        if (data.temostaEnv?.trim().toUpperCase() === 'PROD') {
          this.release = data.releaseVersion;
        } else {
          this.release = `${data.releaseVersion} ${data.temostaEnv}`;
        }
      },
      error: error => {
        console.error(error);
      }
    });
  }

  updateNavigation() {
    this.navigation = [
      ...this.displayNavigation(this.router.routerState.snapshot.url)
    ];
    this.changeDetector.detectChanges();
    this.disableNavigationLinks();
  }

  private disableNavigationLinks() {
    setTimeout(() => {
      const navItems = document.querySelectorAll('nav li a');

      navItems.forEach((link: HTMLAnchorElement) => {
        const href = link.getAttribute('href');
        if (href) {
          const sanitizedHref = href.replace('/', '');
          if (sanitizedHref === 'result' || sanitizedHref === 'cockpit') {
            if (this.resultEnabled) {
              link.style.pointerEvents = 'auto';
              link.style.opacity = '1';
              link.style.cursor = 'pointer';
            } else {
              link.style.pointerEvents = 'none';
              link.style.opacity = '0.5';
              link.style.cursor = 'not-allowed';
            }
          }
        }
      });
    }, 500);
  }

  private formatLanguages(languages: Record<string, string>): ObILanguage[] {
    const isDisplayEnabled = (this.config.locale as {display?: boolean})
      .display;

    return this.config.locale.disabled || isDisplayEnabled === false
      ? []
      : this.config.locale.locales
          .map(locale => this.getLocaleObject(locale))
          .map(locale => ({
            code: locale.locale.split('-')[0],
            id: locale.id
          }))
          .map(locale => ({...locale, label: languages[locale.code]}));
  }

  private async initializeAsync(): Promise<void> {
    this.navigationService.navigationChange$.subscribe(() => {
      this.updateNavigation();
    });

    this.items = this.tree.items.map(item => new ObNavTreeItemModel(item));

    this.kontrollmodus = await this.storage.getKontrollmodus();
    if (this.kontrollmodus === null) {
      this.kontrollmodus = false;
      this.storage.storeKontrollmodus(this.kontrollmodus);
    }
    this.setResultDisplay(await this.resultGuard.hasResult());
    this.setAnalyseEnabled(this.analysisGuard.checkModuleSelected());

    this.wizardSubscription = this.wizard.resultChanged.subscribe({
      next: () => {
        void this.refreshResultDisplayAsync();
      }
    });

    this.directModuleSelectionSubscription =
      this.directModuleSelection.isDirect$.subscribe({
        next: isDirect => {
          this.homeLinkHidden = isDirect;
        }
      });

    this.analysisSubscription =
      this.analyseStartedService.moduleChanged.subscribe({
        next: () => {
          this._analyseEnabled =
            this.analyseStartedService.isForModule2 !== null;
        }
      });

    this.translate.onLangChange.subscribe((next: LangChangeEvent) => {
      this._fileLangKey = next.lang.substring(0, 1);
      this.renderer.setAttribute(
        document.querySelector('html'),
        'lang',
        next.lang
      );
    });

    window.onbeforeunload = async (event: BeforeUnloadEvent) => {
      const hasChange = await this.storage.getUnsavedChanges();
      const senderRoute = (event.currentTarget as Window).location.pathname;

      if (hasChange && !senderRoute.includes('/cockpit/')) {
        return (event.returnValue =
          'You have closed the browser. Do you want to logout from your application?');
      }

      return undefined;
    };
    this.setReleasenumber();
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const url = event.urlAfterRedirects || event.url;

        const main = document.querySelector('main');
        if (main) {
          main.id = 'content';
        }

        this.updatePageState(url);
        this.updateNavigation();
      });
    this.updatePageState(this.router.routerState.snapshot.url);
    this.updateNavigation();
  }

  private async refreshResultDisplayAsync(): Promise<void> {
    this.setResultDisplay(await this.resultGuard.hasResult());
    this.changeDetector.detectChanges();
    this.updateNavigation();
  }

  private updatePageState(url: string): void {
    const body = document.body.classList;

    body.remove(
      'home-active',
      'result-m2-active',
      'analyzer-m2-active',
      'cockpit-m2-active',
      'result-m1-active',
      'analyzer-m1-active',
      'cockpit-m1-active'
    );

    if (url === '/' || url === '/home') body.add('home-active');
    if (url.includes('/result/m2')) body.add('result-m2-active');
    if (url.includes('/analyzer/m2')) body.add('analyzer-m2-active');
    if (url.includes('/cockpit/m2')) body.add('cockpit-m2-active');
    if (url.includes('/result/m1')) body.add('result-m1-active');
    if (url.includes('/analyzer/m1')) body.add('analyzer-m1-active');
    if (url.includes('/cockpit/m1')) body.add('cockpit-m1-active');
  }

  private getLocaleObject(locale: string | ObILocaleObject): ObILocaleObject {
    return (locale as ObILocaleObject).locale
      ? (locale as ObILocaleObject)
      : {locale: locale as string};
  }

  private displayNavigation(route: string): ObINavigationLink[] {
    const navigation: ObINavigationLink[] = [];
    const activeRoute = route.split('?')[0].split('#')[0];
    const isAbsoluteRoute = activeRoute.startsWith('/');

    if (route !== '') {
      if (!this.homeLinkHidden) {
        navigation.push({
          url: 'home',
          label: 'i18n.routes.home.title',
          active:
            activeRoute === '/' || activeRoute === '/home' || !isAbsoluteRoute
        });
      }
      navigation.push({
        url: 'analyzer',
        label: 'i18n.routes.analyzer.title',
        active:
          activeRoute.startsWith('/analyzer') ||
          (!isAbsoluteRoute && this.analyseEnabled && !this.resultEnabled)
      });
      navigation.push({
        url: 'result',
        label: 'i18n.routes.result.title',
        active: activeRoute.startsWith('/result')
      });
      navigation.push({
        url: 'cockpit',
        label: 'i18n.routes.cockpit.title',
        active: activeRoute.startsWith('/cockpit')
      });
    }

    if (!this.resultEnabled) {
      this.disableNavigationLinks();
    }

    return navigation;
  }
}
